import { useMemo } from 'react'
import { useLocation } from 'react-router-dom'

import { MainContent, ModalContext, TopBar } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'
import getQueryParamsFromHash from '@functions/utils/getQueryParamsFromHash'
import parsePermittedQueryParams from '@functions/utils/parsePermittedQueryParams'

import useFilters from '@hooks/useFilters'
import useMainNavigation from '@hooks/useMainNavigation'
import useModals from '@hooks/useModals'
import useSetState from '@hooks/useSetState'

import ManageAdDeploymentModal from '@modals/ManageAdDeploymentModal'
import SelectedLocationsModal from '@modals/SelectedLocationsModal'
import SelectedPlaybooksModal from '@modals/SelectedPlaybooksModal'

import { AdQueueModel } from '@models/types'

import LoggedInUser from '@components/LoggedInUser'
import MainNavigation from '@sections/Admin/components/MainNavigation'

import PageContent from './components/PageContent'

const defaultState = {
  showManageAdDeploymentModal: false,
  showSelectedLocationsModal: false,
  showSelectedPlaybooksModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

// eslint-disable-next-line
const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    ManageAdDeploymentModal: {
      closeModal: () => setState({ showManageAdDeploymentModal: false }),
      deleteAdQueue: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      processAdQueue: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    SelectedLocationsModal: {
      closeModal: () => setState({ showSelectedLocationsModal: false }),
    },
    SelectedPlaybooksModal: {
      closeModal: () => setState({ showSelectedPlaybooksModal: false }),
    },
  }

  return componentCallbacks[component] || {}
}

const AdDeployer = () => {
  const { callbacks: { toggleNavigation } } = useMainNavigation()

  const [state, setState] = useSetState(defaultState)
  const {
    showManageAdDeploymentModal,
    showSelectedLocationsModal,
    showSelectedPlaybooksModal,
  } = state

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  // adType filter in url does not work without this
  useLocation()

  const { adType }: { adType?: string } = parsePermittedQueryParams(getQueryParamsFromHash(), ['adType'])

  const initDefaultFilters = { deploymentStack: 'v2' }
  if (adType) initDefaultFilters.adType = adType

  const filtersPayload = useFilters({
    defaultFilters: initDefaultFilters,
    persistFilters: true,
  })

  const {
    callbacks: filterCallbacks,
    defaultFilters,
    defaultFiltersModifiedAt,
    pageFilters,
  } = filtersPayload

  const modalContext = useModals()
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      bulkDeleteAdQueues: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      bulkDeleteAndRequeueAdQueues: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      showManageAdDeploymentModal: (payload: { adQueue: AdQueueModel }) => {
        setModalData('ManageAdDeploymentModal', payload)
        setState({ showManageAdDeploymentModal: true })
      },
      showSelectedLocationsModal: (payload: { adQueue: AdQueueModel }) => {
        setModalData('SelectedLocationsModal', payload)
        setState({ showSelectedLocationsModal: true })
      },
      showSelectedPlaybooksModal: (payload: { adQueue: AdQueueModel }) => {
        setModalData('SelectedPlaybooksModal', payload)
        setState({ showSelectedPlaybooksModal: true })
      },
      ...filterCallbacks,
    },
    defaultFilters,
  }), [defaultFiltersModifiedAt, modalContext])

  return (
    <>
      <TopBar
        callbacks={{ toggleNavigation }}
        loggedInUserComponent={<LoggedInUser />}
        searchComponent={<TopBar.Search callbacks={{ search: null }} />}
      />

      <MainNavigation />

      <MainContent offset={{ top: TopBar.topBarHeight }}>
        <PageContext.Provider value={pageContext}>
          <ModalContext.Provider value={modalContext}>
            <PageContent pageFilters={pageFilters} />

            <ManageAdDeploymentModal
              callbacks={callbacks('ManageAdDeploymentModal', setState)}
              showModal={showManageAdDeploymentModal}
            />

            <SelectedLocationsModal
              callbacks={callbacks('SelectedLocationsModal', setState)}
              showModal={showSelectedLocationsModal}
            />

            <SelectedPlaybooksModal
              callbacks={callbacks('SelectedPlaybooksModal', setState)}
              showModal={showSelectedPlaybooksModal}
            />
          </ModalContext.Provider>
        </PageContext.Provider>
      </MainContent>
    </>
  )
}

export default AdDeployer
